import { AppMode, RegularFlowQueryParams } from '@customTypes/Generic'
import {
  useChoiceState,
  useChoiceDispatch,
  setSelectedBookable,
  setAppointmentDuration,
  setTreatment,
  setPreferredPractitioners,
  setPatientType,
  setInsuranceType,
  setPatientReferenceIdAndPreferredPractitionerId,
  setPreferredPractitionersAfterFetching,
} from '@context/ChoiceContext'
import { useGapFillerQueryParams, useRecallQueryParams } from './useQueryParams'
import useComponentDidMount from './useComponentDidMount'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import queryString from 'query-string'
import { setContactDetailsField, useContactDetailsDispatch } from '@context/ContactDetailsContext'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'
import { InsuranceTypeOptions } from 'src/config/insuranceTypeOptions'
import { useConfig } from '@context/ConfigurationContext'
import { useState } from 'react'

export const useSetInitialChoice = (mode: AppMode) => {
  const [initialized, setInitialized] = useState(false)
  const { queryParams: gapFillerQueryParams } = useGapFillerQueryParams()
  const { queryParams: recallQueryParams } = useRecallQueryParams()
  const {
    selectedBookableDateAndTime,
    appointmentDuration,
    treatmentBundleTypeReferenceId,
    preferredPractitionerReferenceIds,
  } = useChoiceState()
  const { clinicId } = useConfig()
  const choiceDispatch = useChoiceDispatch()
  const contactDetailsDispatch = useContactDetailsDispatch()
  const queryParams = gapFillerQueryParams || recallQueryParams
  const history = useHistory()

  useComponentDidMount(() => {
    if (mode === AppMode.GAP_FILLER || mode === AppMode.RECALL) {
      const [date, time] = (queryParams?.start ?? '').split('T')

      if (!selectedBookableDateAndTime.date && !selectedBookableDateAndTime.time) {
        choiceDispatch(setSelectedBookable({ ...selectedBookableDateAndTime, date, time, day: date, clinicId }))
      }

      if (!appointmentDuration) {
        choiceDispatch(setAppointmentDuration(parseInt(queryParams?.durationMin || '45', 10)))
      }

      if (!treatmentBundleTypeReferenceId) {
        const treatmentId =
          mode === AppMode.GAP_FILLER
            ? gapFillerQueryParams?.treatmentBundleReferenceId
            : recallQueryParams?.treatmentBundleTypeReferenceId

        choiceDispatch(setTreatment(treatmentId || ''))
      }
      choiceDispatch(setPatientType(PatientTypeOptions.OLD))

      const practitioner =
        gapFillerQueryParams?.practitionerReferenceIds?.length === 1
          ? gapFillerQueryParams?.practitionerReferenceIds[0]
          : recallQueryParams?.practitionerReferenceId

      // NOTICE we need to set practitioner after treatment
      // since treatment data will overwrite practitioner
      if (practitioner && !preferredPractitionerReferenceIds.length) {
        choiceDispatch(setPreferredPractitioners([practitioner]))
      }
    } else if (mode === AppMode.NORMAL) {
      const params: RegularFlowQueryParams = queryString.parse(window.location.search)
      const {
        patientType,
        phoneNr,
        treatmentBundleReferenceId,
        insuranceType,
        patientReferenceId,
        practitionerReferenceIds,
      } = params

      if (treatmentBundleReferenceId) {
        choiceDispatch(setTreatment(treatmentBundleReferenceId))
      }

      if (patientType && Object.values(PatientTypeOptions).includes(patientType)) {
        choiceDispatch(setPatientType(patientType))
      }

      if (patientReferenceId) {
        choiceDispatch(setPatientReferenceIdAndPreferredPractitionerId({ patientReferenceId }))
      }

      if (practitionerReferenceIds) {
        choiceDispatch(setPreferredPractitionersAfterFetching({ practitionerIds: [practitionerReferenceIds] }))
      }

      if (phoneNr) {
        contactDetailsDispatch(setContactDetailsField('phone', phoneNr))
      }

      const hasInsuranceType = insuranceType && Object.values(InsuranceTypeOptions).includes(insuranceType)

      if (hasInsuranceType) {
        choiceDispatch(setInsuranceType(insuranceType))
      }

      if (treatmentBundleReferenceId && patientType && hasInsuranceType) {
        history.push(routes.appointment)
      } else if (treatmentBundleReferenceId) {
        history.push(routes.patient)
      }
    }
    setInitialized(true)
  })

  return initialized
}
